import { Component, OnInit } from "@angular/core";
import { FormBuilder, Validators, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../services/auth.service";
import { ModulesDataService } from "src/app/services/data/modules-data.service";
import { LocalizationDataService } from "src/app/services/data/localization-data.service";
import { catchError, finalize, forkJoin, of, switchMap, tap, throwError } from "rxjs";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  public newUser = false;
  public loginForm: FormGroup;
  public show: boolean = false;
  public errorMessage: string = ''; // Changed to string for better type handling
  private returnUrl: string = 'dashboard/main'; // Initialized with main value
  private redirectUrl: string = 'dashboard/main';
  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private modulesDataService: ModulesDataService,
    private route: ActivatedRoute,
    private localizationDataService: LocalizationDataService
  ) {
    this.loginForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required],
    });
  }

  ngOnInit() {
    // If the user is already logged in, redirect them to the appropriate page
    if (this.authService.isLoggedIn()) {
      this.setReturnUrl(); // Moved logic to a dedicated method for clarity
      this.router.navigate([this.returnUrl]);
    } else {
      this.returnUrl = 'dashboard/main';
    }

    const token = this.route.snapshot.queryParamMap.get('token');
    if (token) {
      this.authService.loginWithSSOToken(token).pipe(
        switchMap(response => {
          const redirectUrl = response?.redirect_url || null; // ✅ Extract redirect_url if available
          this.redirectUrl = redirectUrl ? redirectUrl : this.returnUrl; // ✅ Use redirect_url if available
          return this.authService.getModules();
        }),
        switchMap(modules => {
          this.modulesDataService.saveLoadedModules(modules);
          return this.authService.getAppModules(); // Then, get app modules
        }),
        tap(appModules => {
          this.modulesDataService.saveLoadedAppModules(appModules);
        }),
        switchMap(() => forkJoin([
          this.localizationDataService.getLocalizations(), // Run localization in parallel
          of(true) // Dummy observable to ensure forkJoin works even if there's only one request
        ])),
        finalize(() => {
          this.setReturnUrl();
          this.router.navigate([this.returnUrl]);
        }),
        catchError(err => {
          this.errorMessage = 'Invalid credentials';
          return throwError(() => err);
        })
      ).subscribe();
    }
  }

  login() {
    if (this.loginForm.invalid) {
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }
  
    const { email, password } = this.loginForm.value;
  
    this.authService.login(email, password)
      .pipe(
        switchMap(response => {
          const redirectUrl = response?.redirect_url || null; // ✅ Extract redirect_url if available
          this.redirectUrl = redirectUrl ? redirectUrl : this.returnUrl; // ✅ Use redirect_url if available
          return this.authService.getModules();
        }),
        switchMap(modules => {
          this.modulesDataService.saveLoadedModules(modules);
          return this.authService.getAppModules(); // Then, get app modules
        }),
        tap(appModules => {
          this.modulesDataService.saveLoadedAppModules(appModules);
        }),
        switchMap(() => forkJoin([
          this.localizationDataService.getLocalizations(), // Run localization in parallel
          of(true) // Dummy observable to ensure forkJoin works even if there's only one request
        ])),
        finalize(() => {
          this.setReturnUrl();
          this.router.navigate([this.returnUrl]);
        }),
        catchError(err => {
          this.errorMessage = 'Invalid credentials';
          return throwError(() => err);
        })
      )
      .subscribe();
  }  

  showPassword() {
    this.show = !this.show;
  }

  private setReturnUrl() {
    // Logic to determine where to redirect the user after login
    if (this.authService.isSuperAdmin()) {
      this.returnUrl = '/groups';
    } else if (this.authService.isExternalCompany()) {
      this.returnUrl = '/setup/companies/recruitments';
    } else if (this.route.snapshot.queryParams['returnUrl']) {
      this.returnUrl = this.route.snapshot.queryParams['returnUrl'];
    } else {
      this.returnUrl = this.redirectUrl;
    }
  }
}
